/**
 * Care at the point of injury and at Role 1: the Tab 3 card (R1-07, 2026-10-25, Dr. Tatar).
 *
 * Dr. Tatar: combat lifesaver (CLS) training and the training of the medics in prolonged field care (PFC) are part of the medical force, so
 * they belong to a card of Tab 3, not to Tab 1 (the Tab 1 field is hidden, SHOW_CLS_TRAINING in Tab1_ForceComposition.jsx). CLS training does
 * not give PFC training: they are two separate settings of the same card.
 *   cls_level     share of the soldiers trained as combat lifesavers (LOW / STANDARD / HIGH / ELITE, CLS_TRAINING_LEVELS); acts on the
 *                 first-aid quality of the survival model (resolveClsQuality).
 *   pfc_training  what the Role 1 medics are trained to sustain (NONE / BASIC / INTERMEDIATE / ADVANCED, the bands of PFC_CAPABILITY). The
 *                 prolonged field care level of the force is the LOWER of this training and what the Role 1 facilities hold (pfcFromFacilities.js):
 *                 trained medics without blood or a provider cannot sustain intermediate care, and a well equipped aid station with untrained
 *                 medics does not either.
 * Saved by Tab 3 in medical_configuration.point_of_injury_care. Scenarios saved before the card existed take the Tab 1 CLS value and the
 * default PFC training BASIC (migration in Tab 3).
 * STATUS: EDUCATED GUESS (the default BASIC: NATO medics are trained in TCCC; the PFC course is an add-on, JTS PCC guideline).
 */
import { CLS_TRAINING_LEVELS } from './combatEffectivenessModifiers';

export const POI_CARE_DEFAULTS = Object.freeze({ cls_level: 'STANDARD', pfc_training: 'BASIC' });

export const PFC_TRAINING_LEVELS = Object.freeze({
  NONE:         { label: 'No PFC training', description: 'Medics trained in TCCC only: they can hold a casualty, not treat one over hours' },
  BASIC:        { label: 'Basic PFC', description: 'Extended TCCC: monitoring and intravenous fluids, holding up to about 4 hours' },
  INTERMEDIATE: { label: 'Intermediate PFC', description: 'Full PFC course: blood products, airway, antibiotics, serial assessment, up to about 12 hours' },
  ADVANCED:     { label: 'Advanced PFC (DCR)', description: 'Damage control resuscitation at Role 1, monitoring up to about 24 hours' }
});

export const POI_CARE_STATUS = Object.freeze({ status: 'EDUCATED_GUESS', basis: 'Bands of PFC_CAPABILITY (JTS Prolonged Casualty Care Guidelines); default BASIC is an assumption' });

/**
 * The CLS level the calculation uses: Tab 3 when the card holds one, else the Tab 1 value (failsafe, reported as source).
 * @returns {{level: string|undefined, source: 'TAB3'|'TAB1'}}
 */
export function resolveClsLevel(medicalConfig, forceQuality) {
  const tab3 = medicalConfig?.point_of_injury_care?.cls_level;
  if (tab3 !== undefined && tab3 !== null && tab3 !== '') {
    if (!Object.prototype.hasOwnProperty.call(CLS_TRAINING_LEVELS, tab3)) {
      throw new Error(`resolveClsLevel: unknown cls_level '${tab3}' (valid: ${Object.keys(CLS_TRAINING_LEVELS).join(', ')})`);
    }
    return { level: tab3, source: 'TAB3' };
  }
  return { level: forceQuality?.cls_training_percentage, source: 'TAB1' };
}

/** The PFC training of the Role 1 medics from the card, or null when the card holds none (scenarios made before the card). */
export function pfcTrainingOf(medicalConfig) {
  const v = medicalConfig?.point_of_injury_care?.pfc_training;
  if (v === undefined || v === null || v === '') return null;
  if (!Object.prototype.hasOwnProperty.call(PFC_TRAINING_LEVELS, v)) {
    throw new Error(`pfcTrainingOf: unknown pfc_training '${v}' (valid: ${Object.keys(PFC_TRAINING_LEVELS).join(', ')})`);
  }
  return v;
}
